import { ValidatorFn, Validators } from '@angular/forms';
import {
  getSchemaMin,
  getSchemaMax,
  SchemaControlLike,
} from './schema-min.util';

/**
 * Per-control translation of a form-schema's declared numeric/format
 * constraints into Angular `ValidatorFn`s.
 *
 * ## Why this exists
 *
 * Every XBRL sheet component used to hand-roll its own `getValidators()` /
 * `buildForm()` loop to lift `validators: [...]` entries out of the schema.
 * Those copies drifted: roles 10/11/23 read the min bound from `value`, role 6
 * read it from `min`, role 20 derived it from the control *type*, and role 23
 * silently dropped `min` altogether ("Min validators removed to allow negative
 * values" — which let a `-` be typed into share-count fields the schema marks
 * `min: 0`). Centralising the rule here means a field is bounded by **its own
 * schema entry**, identically, everywhere — and a future sheet cannot quietly
 * forget a declared constraint.
 *
 * ## Granular by design
 *
 * Everything here keys off a *single control*, never a sheet. A sheet with a
 * mix of constrained inputs (e.g. share counts, `min: 0`) and unconstrained
 * inputs (e.g. monetary amounts that may legitimately be negative) gets each
 * input bounded on its own merits — `getSchemaMin` returns `undefined` for the
 * monetary fields, so no `min` is applied to them.
 *
 * ## Layering
 *
 * The Angular-free readers (`getSchemaMin` / `getSchemaMax` / `valueBelowSchemaMin`)
 * live in `schema-min.util.ts` so the DI-free pure validators can share them
 * without importing `@angular`. This file adds only the `ValidatorFn` layer.
 *
 * ## Scope
 *
 * Maps **numeric/format** constraints only — `min`, `max`, `pattern`. It
 * deliberately does NOT add `Validators.required`: required-ness is sometimes
 * conditional (role 6) or derived differently per sheet, so each component
 * keeps owning that decision and simply concatenates the result of
 * {@link mapSchemaValidators} with its own required logic.
 */

// Re-export the pure readers so component callers have a single import site.
export {
  getSchemaMin,
  getSchemaMax,
  valueBelowSchemaMin,
} from './schema-min.util';
export type { SchemaControlLike, SchemaValidatorDef } from './schema-min.util';

/**
 * Map a control's schema-declared numeric/format constraints into Angular
 * `ValidatorFn`s — `min`, then `max`, then `pattern`. Returns an empty array
 * for a control with no such constraints. Does NOT include
 * `Validators.required` (see the file header).
 */
export function mapSchemaValidators(control: SchemaControlLike | null | undefined): ValidatorFn[] {
  const validators: ValidatorFn[] = [];

  const min = getSchemaMin(control);
  if (min !== undefined)
    validators.push(Validators.min(min));

  const max = getSchemaMax(control);
  if (max !== undefined)
    validators.push(Validators.max(max));

  const patternDef = control?.validators?.find((validator) => validator?.name === 'pattern');
  if (patternDef?.pattern)
    validators.push(Validators.pattern(patternDef.pattern));

  return validators;
}
